import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { searchVideos } from '../api/vimeoApi';
import { DEBOUNCE_MS, MIN_QUERY_LEN } from '../constants';
import { useRateLimiter } from './useRateLimiter';

export function useVimeoSearch(query) {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [msUntilReset, setMsUntilReset] = useState(0);

  const { canRequest, recordRequest, msUntilReset: getReset } = useRateLimiter();
  const cancelSourceRef = useRef(null);

  useEffect(() => {
    if (query.length < MIN_QUERY_LEN) {
      setResults([]);
      setError(null);
      setIsRateLimited(false);
      return () => {};
    }

    const timer = setTimeout(async () => {
      if (!canRequest()) {
        setIsRateLimited(true);
        setMsUntilReset(getReset());
        return;
      }

      if (cancelSourceRef.current) cancelSourceRef.current.cancel();
      cancelSourceRef.current = axios.CancelToken.source();

      setIsLoading(true);
      setError(null);
      setIsRateLimited(false);

      try {
        recordRequest();
        const data = await searchVideos(query, cancelSourceRef.current.token);
        setResults(data || []);
      } catch (err) {
        if (err.message !== 'CANCELED REQUEST') {
          setError('Search failed. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, canRequest, recordRequest, getReset]);

  return { results, isLoading, error, isRateLimited, msUntilReset };
}
